import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import type { LocalDevice } from '@this+that/models';

import { GET_USER_MCP_CONNECTIONS } from './mcpConnections.service';

const LOCAL_DEVICE_FIELDS = gql`
  fragment LocalDeviceFields on LocalDevice {
    userId
    deviceId
    name
    mcpConnectionId
    lastSeenAt
    lastRegisteredUrl
    createdAt
    updatedAt
    isConnected
  }
`;

const GET_MY_LOCAL_DEVICES = gql`
  ${LOCAL_DEVICE_FIELDS}
  query getMyLocalDevices {
    getMyLocalDevices {
      ...LocalDeviceFields
    }
  }
`;

const APPROVE_DEVICE_PAIRING = gql`
  ${LOCAL_DEVICE_FIELDS}
  mutation approveDevicePairing($code: String!) {
    approveDevicePairing(code: $code) {
      ...LocalDeviceFields
    }
  }
`;

const REVOKE_LOCAL_DEVICE = gql`
  mutation revokeLocalDevice($deviceId: String!) {
    revokeLocalDevice(deviceId: $deviceId)
  }
`;

interface GetMyLocalDevicesData {
  getMyLocalDevices: LocalDevice[];
}

interface ApproveDevicePairingData {
  approveDevicePairing: LocalDevice;
}

interface ApproveDevicePairingVars {
  code: string;
}

interface RevokeLocalDeviceData {
  revokeLocalDevice: boolean;
}

interface RevokeLocalDeviceVars {
  deviceId: string;
}

export interface UseGetMyLocalDevicesOptions {
  skip?: boolean;
}

export function useGetMyLocalDevices(options?: UseGetMyLocalDevicesOptions) {
  return useQuery<GetMyLocalDevicesData>(GET_MY_LOCAL_DEVICES, {
    skip: options?.skip === true,
  });
}

export function useApproveDevicePairing() {
  return useMutation<ApproveDevicePairingData, ApproveDevicePairingVars>(
    APPROVE_DEVICE_PAIRING,
    {
      refetchQueries: [{ query: GET_MY_LOCAL_DEVICES }],
    },
  );
}

export function useRevokeLocalDevice() {
  return useMutation<RevokeLocalDeviceData, RevokeLocalDeviceVars>(
    REVOKE_LOCAL_DEVICE,
    {
      refetchQueries: [
        { query: GET_MY_LOCAL_DEVICES },
        { query: GET_USER_MCP_CONNECTIONS },
      ],
    },
  );
}

export { GET_MY_LOCAL_DEVICES };
